// ═══════════════════════════════════════════════════════════════
// OpenGov EMC — useActiveDisaster Hook
// Resolves the PA disaster linked to the global active incident
// Returns the disaster and a filtered projects list
// ═══════════════════════════════════════════════════════════════

import { useMemo } from 'react';
import { useAuthStore } from '../store/authStore';
import { usePAGrantsStore } from '../store/paGrantsStore';
import type { PADisaster } from '../types/pagrants';

/** Returns the PA disaster linked to the active incident, or null */
export function useActiveDisaster(): PADisaster | null {
  const activeIncident = useAuthStore(s => s.activeIncident);
  const disasters = usePAGrantsStore(s => s.disasters);

  return useMemo(() => {
    if (!activeIncident || disasters.length === 0) {
      console.log('[useActiveDisaster] No match — activeIncident:', activeIncident?.id, activeIncident?.name, '| disasters:', disasters.length);
      return null;
    }
    const match = disasters.find(d => String(d.incidentId) === String(activeIncident.id)) ?? null;
    console.log('[useActiveDisaster] activeIncident:', activeIncident.id, activeIncident.name,
      '| disasters:', disasters.map(d => `${d.Rid}:${d.incidentId}`).join(','),
      '| match:', match?.disasterName ?? 'NONE');
    return match;
  }, [activeIncident, disasters]);
}

/** Filter any array by disasterId when an active disaster is set */
export function useDisasterFilter<T extends { disasterId?: string }>(items: T[]): T[] {
  const activeDisaster = useActiveDisaster();

  return useMemo(() => {
    if (!activeDisaster) return [] as T[];
    const filtered = items.filter(i => String(i.disasterId) === String(activeDisaster.Rid));
    console.log('[useDisasterFilter] Filtering', items.length, '→', filtered.length, 'for disaster Rid:', activeDisaster.Rid);
    return filtered;
  }, [items, activeDisaster]);
}
